import { openDB } from "idb";
import api from "../services/api";

const DB_NAME = "better-govern-offline";
const DB_VERSION = 1;
const STORE_NAME = "pendingGrievances";
const MAX_PENDING = 10;

function getDB() {
  return openDB(DB_NAME, DB_VERSION, {
    upgrade(db) {
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, {
          keyPath: "id",
          autoIncrement: true,
        });
      }
    },
  });
}

/**
 * Save a grievance for later sync.
 * @param {Object} entry
 * @param {Object} entry.formData       — serializable form field values
 * @param {Blob[]} entry.attachmentBlobs — raw file blobs (not yet on S3)
 * @param {string[]} entry.attachmentNames — original file names, parallel to blobs
 * @param {Blob|null} entry.userPhotoBlob
 * @param {Blob|null} entry.mapSnapshotBlob
 * @param {Object|null} entry.location   — { lat, lng }
 * @returns {Promise<number>} the auto-generated id
 */
export async function savePending(entry) {
  const db = await getDB();
  const count = await db.count(STORE_NAME);
  if (count >= MAX_PENDING) {
    throw new Error(
      `Offline limit reached (${MAX_PENDING}). Please sync existing submissions first.`,
    );
  }
  return db.add(STORE_NAME, {
    ...entry,
    savedAt: new Date().toISOString(),
  });
}

/** Get all pending grievances (oldest first). */
export async function getAllPending() {
  const db = await getDB();
  return db.getAll(STORE_NAME);
}

/** Delete a pending grievance by id (after successful sync). */
export async function deletePending(id) {
  const db = await getDB();
  return db.delete(STORE_NAME, id);
}

/** How many items are waiting to sync. */
export async function getPendingCount() {
  const db = await getDB();
  return db.count(STORE_NAME);
}

/**
 * Auto-sync all pending grievances as unverified.
 * Uploads blobs to S3, submits to backend, removes from IndexedDB.
 * @returns {Promise<{ synced: number, failed: number }>}
 */
export async function syncAllPending() {
  const items = await getAllPending();
  if (items.length === 0) return { synced: 0, failed: 0 };

  let synced = 0;
  let failed = 0;

  for (const item of items) {
    try {
      // 1. Upload attachments
      let uploadedAttachments = [];
      if (item.attachmentBlobs?.length > 0) {
        const fd = new FormData();
        item.attachmentBlobs.forEach((blob, i) => {
          fd.append("files", blob, item.attachmentNames?.[i] || `file_${i}`);
        });
        const { data } = await api.post("/api/upload/files", fd, {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 120000,
        });
        uploadedAttachments = data.files;
      }

      // 2. Upload user photo
      let uploadedPhoto = { s3Key: "", url: "" };
      if (item.userPhotoBlob) {
        const fd = new FormData();
        fd.append("photo", item.userPhotoBlob, "user_photo.jpg");
        const { data } = await api.post("/api/upload/photo", fd, {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 60000,
        });
        uploadedPhoto = data;
      }

      // 3. Upload map snapshot
      let uploadedMapSnapshot = { s3Key: "", url: "" };
      if (item.mapSnapshotBlob) {
        const fd = new FormData();
        fd.append("mapSnapshot", item.mapSnapshotBlob, "map_snapshot.png");
        const { data } = await api.post("/api/upload/map-snapshot", fd, {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 60000,
        });
        uploadedMapSnapshot = data;
      }

      // 4. Submit grievance
      const payload = {
        ...item.formData,
        isVerified: false,
        submissionMode: "offline-unverified",
        offlineSubmittedAt: item.savedAt,
        attachments: uploadedAttachments,
        userPhoto: uploadedPhoto,
        mapSnapshot: uploadedMapSnapshot,
      };

      if (item.location) {
        payload.location = {
          type: "Point",
          coordinates: [item.location.lng, item.location.lat],
        };
      }

      await api.post("/api/grievances", payload);

      // 5. Remove from IndexedDB
      await deletePending(item.id);
      synced++;
    } catch (err) {
      console.error(`Failed to sync offline grievance ${item.id}:`, err);
      failed++;
    }
  }

  return { synced, failed };
}
